import React, { useEffect, useReducer, useRef, useState } from "react"
import schema from "./schema"
import { unwrapNonNull, unwrapNonNullTypeName } from "../util"
import { resolveValidatorByType } from "./validating"
import { resolveByType } from "./rendering"


/**
 *
 * @type {Map<String, Scratch>}
 */
const scratches = new Map()

/**
 * Maps strings in the form "MyType.myField" to a custom rendering function for that field.
 *
 *
 * @type {Map<String, Function>}
 */
const renderers = new Map()

/**
 * Maps strings in the form "MyType.myField" to a custom validator function for that field.
 *
 * (ctx, name, value, type, typeValidator) => ...
 *
 * @type {Map<String, function>}
 */
const validators = new Map()

export class TypeContext
{
    type
    typeDef

    constructor(type)
    {
        if (typeof type !== "string" || !type)
        {
            throw new Error("Need type")
        }

        this.type = type

        const typeDef = schema.types.find(td => td.name === type)
        if (!typeDef)
        {
            throw new Error("Unknown composite type: " + type);
        }
        this.typeDef = typeDef
    }

    get fields()
    {
        return this.typeDef.fields || this.typeDef.inputFields
    }

    getFieldDef(name)
    {
        const result = this.fields.find(fd => fd.name === name)
        if (!result)
        {
            throw new Error("Could not find field '" + name + "' in '" + this.type)
        }
        return result;
    }

    validate(fd, value)
    {
        const { name } = fd

        let validator = validators.get(typeKey(this.type, name))
        const typeValidator = resolveValidatorByType(fd.type)
        if (validator)
        {
            return validator(this, fd, value, typeValidator)
        }
        return typeValidator(this, fd, value)
    }
}



function typeKey(objectType, name)
{
    return objectType + "." + name
}


function resolveFieldRender(objectType, name, type)
{
    const renderer = renderers.get(typeKey(objectType, name))
    const byType = resolveByType(type)
    if (!renderer)
    {
        return byType
    }
    return (ctx, fd, value) => renderer(ctx, fd, value, byType)
}

const EMPTY = []

function toFormValue(formContext, name, type, value)
{
    if (value === null)
    {
        return ""
    }

    const unwrapped = unwrapNonNullTypeName(type)
    switch (unwrapped)
    {
        case "Boolean":
            return value;
        case "Int":
            return String(value)
    }
    return value
}


function createSnapshot(formContext)
{
    const out = {}
    const { fields } = formContext.typeContext
    for (let i = 0; i < fields.length; i++)
    {
        const { name } = fields[i]
        out[name] = formContext.obj[name]
    }
    return out;
}


/**
 * Encapsulates temporary values for a form. It is stored in a map keyed by form id.
 */
class Scratch {
    /**
     * Contains temporary field values used when the current field validating fails and we don't want to write back
     * faulty values but the user input must be preserved.
     */
    tmp

    /**
     * Contains the errors within the form. (name -> error)
     * @type {Map<any, any>}
     */
    errors = new Map()
    
    constructor(snapshot)
    {
        this.tmp = snapshot
    }

    getError(name)
    {
        return this.errors.get(name)
    }
}


const DEFAULTS = {
    fieldNames: []
}

function getOption(opts, name)
{
    if (opts && opts.hasOwnProperty(name))
    {
        return opts[name]
    }
    else
    {
        return DEFAULTS[name]
    }

}

/**
 * Encapsulates the environment of the current form
 */
class FormContext
{
    /**
     * Unique form id
     *
     * @type {String}
     */
    id

    /**
     * Type name of the complex base type
     *
     * @type {String}
     */
    type

    /**
     * Base object. Will be "edited" by producing new
     *
     * @type {object}
     */
    obj
    setter
    forceUpdate

    _ctx = null
    constructor(formId, type, obj, setter, forceUpdate)
    {
        this.id = formId
        this.type = type
        this.obj = obj
        this.setter = setter
        this.forceUpdate = forceUpdate
    }

    get typeContext()
    {
        if (this._ctx)
        {
            return this._ctx;
        }
        const ctx = new TypeContext(this.type)
        this._ctx = ctx
        return ctx
    }

    get scratch()
    {
        let scratch = scratches.get(this.id)
        if (!scratch)
        {
            scratch = new Scratch(
                createSnapshot(this)
            )
            scratches.set(this.id, scratch)
        }
        return scratch
    }

    render(opts)
    {
        const fieldNames = getOption(opts, "fieldNames")

        const objectType = this.typeContext.type

        const { scratch } = this


        let { fields } = this.typeContext

        if (fieldNames.length)
        {
            fields = fields.filter(
                fd => fieldNames.indexOf(fd.name) >= 0
            )
        }
        
        return (
            <div className="form-block">
                {
                    fields.map((fd, i) => {

                        //console.log("Render", fd)

                        const { name } = fd
                        const type = unwrapNonNull(fd.type).name
                        const fieldRenderer = resolveFieldRender(objectType, name, fd.type ? type : type + "!")

                        let error = scratch.getError(name)
                        const value = error ? scratch.tmp[name] : toFormValue(this, name, type, this.obj[name]);
                        if (fieldRenderer)
                        {
                            return (
                                <div key={ i } className="form-field">
                                    {
                                        fieldRenderer(this, fd, value)
                                    }
                                    {
                                        error && (
                                            <span key={ i } className="error">
                                                { error }
                                            </span>
                                        )
                                    }
                                </div>
                            )
                        }
                        return false
                    })
                }
            </div>
        )
    }


    clearErrors(name)
    {
        const { errors } = this.scratch
        if (!name)
        {
            errors.clear()
            this.forceUpdate()
        }
        else
        {
            errors.delete(name)
        }
    }

    addError(name, msg)
    {
        console.log("addError", name, msg)
        const { scratch } = this
        let { errors } = scratch
        errors.set(name, msg)
        this.forceUpdate()
    }

    setValue(fd, value)
    {
        const { name } = fd

        const [converted, error] = this.typeContext.validate(fd, value)
        if (error)
        {
            this.scratch.tmp[name] = value
            this.addError(name, error)
        }
        else
        {
            this.clearErrors(name)
            this.setter({
                ... this.obj,
                [name]: converted
            })
        }
    }
}


/**
 * Registers a new field renderer
 *
 * The custom renderer receives 3 arguments ( ctx, fd, value)
 *
 * ctx is the current form context, fd is a GraphQL field type definition and value is the current value of the field.
 *  
 * @param {String} objectType   Complex type name
 * @param {String} name         Field name within that type
 * @param {Function} renderer   renderer function ( (ctx, fd, value) => ... )
 */
export function registerRenderer(objectType, name, renderer)
{
    renderers.set(typeKey(objectType, name), renderer)
}


/**
 * Registers a new validator
 *
 * The validator function receives 4 arguments
 *
 * ctx is the current form context, name is the current field name, value the current unconverted value and typeValidator
 * is the type validator the system would use if your validator wasn't defined. You can delegate basic validations to it
 * to e.g. first ensure that it is a number and then do further checks on that number.
 *
 * @param {String} objectType    Complex type name
 * @param {String} name          Field name within that type
 * @param {Function} validator      validator function ( (ctx, name, value, type, typeValidator) => ... )
 */
export function registerValidator(objectType, name, validator)
{
    validators.set(typeKey(objectType, name), validator)
}

let contextCounter = 0

export default function useForm(type, obj, setter)
{
    const formId = useRef("form-" + contextCounter++ )

    useEffect(() => {
        return () => {
            scratches.delete(formId.current)
        }
    }, [])

    const [, forceUpdate] = useReducer(n => {
        console.log("forceUpdate:", n)
        return n + 1
    }, 0)

    return new FormContext(
        formId.current,
        type,
        obj,
        setter,
        forceUpdate
    )
}
